import {Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation, ChangeDetectionStrategy} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import {Field, ProductMutation} from '../../interfaces/field';
import {LangChangeEvent, TranslateService} from "@ngx-translate/core";
import {Subscription} from "rxjs";
import {StateService} from "../../services/state.service";
import {CurrencyPipe} from "@angular/common";
import {UnicornPipe} from "../../pipes/unicorn.pipe";

@Component({
    selector: 'app-number-field',
    templateUrl: './number-field.component.html',
    encapsulation: ViewEncapsulation.Emulated,
    styleUrls: ['./number-field.component.scss'],
    providers: [CurrencyPipe, UnicornPipe],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NumberFieldComponent implements OnInit {
  @Input() config: Field;
  @Input() control: UntypedFormGroup;
  @Output() openHelpModal: EventEmitter<any> = new EventEmitter<any>();

  hasErrors = false;
  placeholder = '';
  description = '';
  productMutation: ProductMutation;

  min: number;
  max: number;
  faMinus = 'minus';
  faPlus = 'plus';

  constructor(private translate: TranslateService,
              private state: StateService,
              private currencyPipe: CurrencyPipe,
              private unicornPipe: UnicornPipe) {
  }

  ngOnInit() {
    const _self = this;
    this.min = this.config.min || 0;
    this.max = this.config.max || undefined;

    // Default to the minimum when the field has no value yet. Without this the box
    // is empty and incrementing an empty/undefined value concatenates strings
    // ('' + 1 -> '1' -> '11') instead of counting.
    const control = this.control.controls[this.config.property];
    if (control && (control.value === null || control.value === undefined || control.value === '')) {
      control.setValue(this.min);
    }

    this.defineDescription();

    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      _self.defineDescription();
    });
  }

  defineDescription(): void {
    if(!this.config.label) {
      const self = this;
      setTimeout(function () {
        self.defineDescription();
      }, 100);
      return;
    }

    if (this.config.product) {
      const amount = this.config.product.amount;
      if (this.config.product && this.config.product.type === 'airportDepartureJob') {
        if(this.state.airportDestination && !this.control.controls['returnTrip'].value) {
          delete this.config.product;
        }
      } else if(this.config.product && this.config.product.type === 'airportDestinationJob') {
        if(this.state.airportDeparture && !this.control.controls['returnTrip'].value) {
          delete this.config.product;
        }
      }
    }

    if (this.config.product) {
      this.productMutation = this.config.product;
      const amount = (this.control.controls['returnTrip'].value && this.productMutation.returnAmount ?
        this.productMutation.returnAmount :
        this.productMutation.amount);
      const amountFormatted: any = this.currencyPipe.transform((amount / 100), this.state.currency, 'symbol', '1.2-2');
      this.description = `${this.translate.instant(this.config.label)} (${amountFormatted} ${this.translate.instant('per_item')})`;
    } else {
      this.description = this.translate.instant(this.config.label);
    }
  }

  mutate(add: boolean = true) {
    const control = this.control.controls[this.config.property];
    // Coerce to a number so +/- count instead of concatenating strings, and fall
    // back to the minimum when the current value is empty/NaN.
    const current = Number(control.value);
    const base = isNaN(current) ? this.min : current;
    const newValue = add ? base + 1 : base - 1;
    if (add && (this.max === undefined || newValue <= this.max)) {
      control.setValue(newValue);
    } else if (!add && newValue >= this.min) {
      control.setValue(newValue);
    }
  }

  toggleHelpModal(helpTitleTag: string, helpTextTag: string, type?: string, meta?: any) {
    this.openHelpModal.emit({
      helpTitleTag,
      helpTextTag,
      type,
      meta,
    });
  }

  protected readonly faInfoCircle = 'info-circle';
}
